/**
 * The editor's oracle row: the async, advisory half of "is this kitchen
 * playable?".
 *
 * The checklist above it is the engine's, run in the tab, and includes the
 * save-blocking item-flow proof. Once that passes, the draft is sent to the
 * server (`POST /api/oracle/check`), which plays it with the oracle at a
 * *draft* budget and answers with the round it found. The row says what that
 * was, always with the budget it was found under, and is advice either way:
 *
 *   ✓  9 dishes (3 simple / 6 complex), 240 points · draft check (500 expansions)
 *   !  the oracle served no dish in 5:00 (0 points) · draft check (500 expansions)
 *
 * A search that finds nothing is strong evidence, not proof — the proof is
 * the item-flow row — so neither answer ever disables Save. No row at all
 * while the draft cannot be saved (no check is sent), and none for
 * `impossible`, which the item-flow row has already said with a ✗.
 *
 * Pure: the page holds the state and the fetch; this decides the words.
 */

import type { OracleSummary } from '@overcooked/shared'
import { points } from '../oracle/summary'

/** The rule's name in the checklist. */
export const ORACLE_ROW_RULE = 'an order can be completed (oracle)'

/** Where a draft's oracle check stands. */
export type OracleCheck =
  /** Nothing sent: the draft cannot be saved yet, or the page has not loaded. */
  | { kind: 'idle' }
  | { kind: 'pending' }
  /** A room is in a round, so the server did not take the draft: ask again after `ORACLE_QUEUED_RETRY_MS`. */
  | { kind: 'queued' }
  | { kind: 'done'; summary: OracleSummary }
  /** The server's queue or limiter said 429: try again later. */
  | { kind: 'busy'; message: string }
  | { kind: 'error'; message: string }

/** How long a `queued` draft waits before it is asked about again. */
export const ORACLE_QUEUED_RETRY_MS = 5_000

/** One checklist row, as `<li data-rule="oracle">` draws it. */
export interface OracleRowView {
  rule: string
  /** `data-ok`: `yes`, `no`, or `pending` while the server works. */
  state: 'yes' | 'no' | 'pending'
  mark: '✓' | '!' | '…'
  detail: string
}

/** 300000 -> "5:00". */
export function roundClock(ms: number): string {
  const total = Math.round(ms / 1000)
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

/** "draft check (500 expansions)", plus "(truncated)" when the safety abort cut it short. */
function budget(summary: OracleSummary): string {
  return `${summary.budgetLabel}${summary.result?.truncated ? ' (truncated)' : ''}`
}

/** The row for a check, or `null` when there is no row to show. */
export function oracleRow(check: OracleCheck, roundLengthMs: number): OracleRowView | null {
  switch (check.kind) {
    case 'idle':
    case 'busy':
    case 'error':
      return null
    case 'pending':
      return { rule: ORACLE_ROW_RULE, state: 'pending', mark: '…', detail: 'the oracle is playing this draft…' }
    case 'queued':
      return { rule: ORACLE_ROW_RULE, state: 'pending', mark: '…', detail: 'queued until the round ends' }
    case 'done': {
      const { summary } = check
      if (summary.status === 'impossible') return null
      if (summary.status === 'failed' || !summary.result) {
        return {
          rule: ORACLE_ROW_RULE,
          state: 'no',
          mark: '!',
          detail: `the oracle could not finish this draft (${summary.failure?.error ?? 'no result'}) · ${budget(summary)}`,
        }
      }
      const { result } = summary
      const simple = result.metrics?.simpleServed ?? 0
      const complex = result.metrics?.complexServed ?? 0
      const served = result.metrics ? simple + complex : Object.values(result.servedByDish).reduce((n, k) => n + k, 0)
      if (served === 0) {
        return {
          rule: ORACLE_ROW_RULE,
          state: 'no',
          mark: '!',
          detail: `the oracle served no dish in ${roundClock(roundLengthMs)} (${points(result.score)}) · ${budget(summary)}`,
        }
      }
      return {
        rule: ORACLE_ROW_RULE,
        state: 'yes',
        mark: '✓',
        detail:
          `${served} dish${served === 1 ? '' : 'es'} (${simple} simple / ${complex} complex), ` +
          `${points(result.score)} · ${budget(summary)}`,
      }
    }
  }
}

/**
 * The sentence under the checklist when there is no row to say it: busy, or
 * the request failed. A busy answer is the server's own sentence, which
 * already says what to do ("Try again in a moment.", "Wait a minute.").
 */
export function oracleHint(check: OracleCheck): string | null {
  if (check.kind === 'busy') return check.message
  if (check.kind === 'error') return `The oracle couldn't check this draft. ${check.message}`
  return null
}

type Fetcher = (input: string, init?: RequestInit) => Promise<Response>

/** A 429 with no sentence of its own: the server's busy sentence, `oracle/routes.ts`. */
const ORACLE_BUSY = 'The oracle is busy with other kitchens. Try again in a moment.'

/** Ask the server to play a draft. Never throws; an aborted request comes back as `idle`. */
export async function requestOracleCheck(
  name: string,
  text: string,
  options: { fetcher?: Fetcher; signal?: AbortSignal } = {},
): Promise<OracleCheck> {
  const fetcher = options.fetcher ?? fetch
  let response: Response
  try {
    response = await fetcher('/api/oracle/check', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ name, text }),
      signal: options.signal,
    })
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') return { kind: 'idle' }
    return { kind: 'error', message: `Couldn't reach the server (${(err as Error).message}).` }
  }
  const body = (await response.json().catch(() => null)) as (OracleSummary & { error?: string }) | null
  if (response.status === 202) return { kind: 'queued' }
  if (response.status === 429) return { kind: 'busy', message: body?.error ?? ORACLE_BUSY }
  if (!response.ok || !body) {
    return { kind: 'error', message: body?.error ?? `The server returned an error (${response.status}).` }
  }
  return { kind: 'done', summary: body }
}

/** Wait `ms`, or less if `signal` aborts first. */
function pause(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) return resolve()
    const timer = setTimeout(done, ms)
    function done(): void {
      clearTimeout(timer)
      signal?.removeEventListener('abort', done)
      resolve()
    }
    signal?.addEventListener('abort', done)
  })
}

/**
 * Ask about a draft, and while the server answers `queued` (a round is on),
 * ask again every `ORACLE_QUEUED_RETRY_MS`. Every answer goes to `onCheck`,
 * the `queued` ones too, so the row can say so. Nothing is reported once
 * `signal` aborts — a newer edit has its own question.
 */
export async function checkUntilAnswered(
  name: string,
  text: string,
  onCheck: (check: OracleCheck) => void,
  options: { fetcher?: Fetcher; signal?: AbortSignal; wait?: (ms: number, signal?: AbortSignal) => Promise<void> } = {},
): Promise<void> {
  const wait = options.wait ?? pause
  for (;;) {
    const check = await requestOracleCheck(name, text, options)
    if (options.signal?.aborted) return
    onCheck(check)
    if (check.kind !== 'queued') return
    await wait(ORACLE_QUEUED_RETRY_MS, options.signal)
    if (options.signal?.aborted) return
  }
}
